import React, { useEffect, useRef, useState } from 'react';
import { DefaultStreamChatGenerics, useChannelStateContext, useChatContext, useChannelActionContext } from 'stream-chat-react';
import './MessagingChannelHeader.css';
import { BlockUserIcon, UnBlockUserIcon, ChevronLeft, UnmuteChannelIcon, MuteChannelIcon } from '../../assets';
import { AvatarChannelView } from '../';

import type { StreamChatGenerics } from '../../types';
import { ChatPageType } from '../../enums/ChatPageType';
import { useBlockUnblockContext } from '../../context/BlockUnblock';
import { useAlertContext } from '../../context/AlertContext';
import { useUserConnection } from '../../hooks/useUserConnection';
import Interop from '../../interfaces/Interop';
import { ClientCredential } from '../../models/ClientCredential';
import { StreamChat } from 'stream-chat';
import { useChannelContext } from '../ChannelContext/ChannelContext';

type Props = {
  theme: string;
  toggleMobile: () => void;
  isBlocked: boolean;
  isMuted: boolean;
  interop: Interop;
  isBlockIconVisible: boolean;
  languageCode: any;
  platformType: string;
  UpdateBlockUnblockState: (value: boolean) => void;
  UpdateMutedUnMutedState: (value: boolean) => void;

};


const MessagingChannelHeader = (props: Props) => {
  const { theme, platformType, interop, toggleMobile, UpdateBlockUnblockState, UpdateMutedUnMutedState, isBlockIconVisible, languageCode } = props;
  const { channel } = useChannelStateContext();
  
  /**
   * STATE MANAGEMENT
   * - channelName: Display name from channel data
   * - isEditing: Whether name is being edited (legacy, not used)
   * - title: Fallback name from member info
   * - refreshToken: Credentials for token refresh
   * - chatClient: Client instance after refresh
   */
  const [channelName, setChannelName] = useState(channel.data?.name || '');
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  var isBlocked = props.isBlocked;
  var isMuted = props.isMuted;

  const { toggleBlock } = useBlockUnblockContext();
  const { setShowAlert, setAlertMessage } = useAlertContext();
  const apiKey = process.env.REACT_APP_STREAM_KEY || "";
  const [refreshToken, setRefreshToken] = useState<ClientCredential>();
  
  
  const [chatClient, setChatClient] = useState<StreamChat | null>(null);
  const { client, setActiveChannel } = useChatContext<DefaultStreamChatGenerics>();
  const { selectedChannel, setSelectedChannel } = useChannelContext();
  const { markRead } = useChannelActionContext<DefaultStreamChatGenerics>();

  /**
   * Get other members (excluding current user)
   * Used for DM channel names and block actions
   */
  const members = Object.values(channel.state.members || {}).filter(
    (member) => member.user?.id !== client?.user?.id,
  );

  /**
   * FUNCTION: PerformBackNavigation
   * Handles back button click
   * Marks messages as read, clears active channel, shows channel list
   * FLOW:
   * 1. Mark all messages in channel as read
   * 2. Set navigation flag (prevents ChannelInner side effects)
   * 3. Toggle mobile view (show channel list)
   * 4. Clear active/selected channel
   * 5. Reset flag after 100ms
   */
  const PerformBackNavigation = () => {
    markRead();
    
    (window as any).__isNavigatingBack = true;
    toggleMobile()
    setActiveChannel(undefined);
    setSelectedChannel(null);
    
    setTimeout(() => {
      (window as any).__isNavigatingBack = false;
    }, 100);
  }

  /**
   * FUNCTION: blockUnblockUser
   * Toggles block status for DM channel user
   * UNBLOCK FLOW:
   * 1. Call client.unBlockUser()
   * 2. Unfreeze channel (set frozen: false)
   * 3. Show alert "User unblocked!"
   * 4. Update context and send event
   * BLOCK FLOW:
   * 1. Call client.blockUser()
   * 2. Freeze channel (set frozen: true)
   * 3. Show alert "User blocked!"
   * 4. Update context and send event
   * ERROR HANDLING:
   * If error code 40 (auth error), refresh token and retry
   */
  const blockUnblockUser = async (blockedUserId: string) => {
    if (isBlocked) {
      try {
        await client?.unBlockUser(blockedUserId, client?.user?.id).then(
          _ => {
            UpdateBlockUnblockState(false)
          }
        );

        isBlocked = false;
        await channel.updatePartial({ set: { frozen: false } });


        setAlertMessage(languageCode['User unblocked!']);
        setShowAlert(true);  // Show the alert
        toggleBlock(blockedUserId);
        await channel.sendEvent({ type: 'all', text: 'user unblocked' });
      } catch (err: any) {
        if (err.code == 40) {
          try {
            await interop.GetCredentials().then(async credentialData=>{
              setRefreshToken(credentialData);
            });
          } catch (refreshError) {
            console.error('Error during refresh token process:', refreshError);
          }

        }
      }
    } else {
      try {
        await client?.blockUser(blockedUserId, client?.user?.id).then(_=> {
          UpdateBlockUnblockState(true)
        });
        await channel.updatePartial({ set: { frozen: true } });
        setAlertMessage(languageCode['User blocked!']);
        setShowAlert(true);  // Show the alert
        toggleBlock(blockedUserId);
        await channel.sendEvent({ type: 'all', text: 'user blocked' });
        isBlocked = true;
      } catch (err: any) {
        if (err.code == 40) {
          try {
            await interop.GetCredentials().then(async credentialData=>{
              setRefreshToken(credentialData);
            });
          } catch (refreshError) {
            console.error('Error during refresh token process:', refreshError);
          }
        }
      }
    }
  }

  /**
   * FUNCTION: muteUnmuteChannel
   * Toggles mute status for group channels
   * MUTED STATE:
   * - Calls channel.unmute()
   * - Shows "Channel unmuted!" alert
   * UNMUTED STATE:
   * - Calls channel.mute()
   * - Shows "Channel muted!" alert
   * ERROR HANDLING:
   * If error code 40 (auth error), refresh token
   */
  const muteUnmuteChannel = () => {

    if (isMuted) {
      channel.unmute({ user_id: client?.user?.id })
        .then(_ => UpdateMutedUnMutedState(false)).catch(async (e: any) => {
          if (e.code == 40) {
            try {
              await interop.GetCredentials().then(async credentialData=>{
                setRefreshToken(credentialData);
              });
            } catch (refreshError) {
              console.error('Error during refresh token process:', refreshError);
            }
          }
        });
      setAlertMessage(languageCode['Channel unmuted!']);
      setShowAlert(true);  // Show the alert
      isMuted = false;

    } else {
      channel.mute({ user_id: client?.user?.id })
        .then(_ => UpdateMutedUnMutedState(true)).catch(async (e: any) => {
          if (e.code == 40) {
            try {
              await interop.GetCredentials().then(async credentialData=>{
                setRefreshToken(credentialData);
              });
            } catch (refreshError) {
              console.error('Error during refresh token process:', refreshError);
            }
          }
        });
      setAlertMessage(languageCode['Channel muted!']);
      setShowAlert(true);  // Show the alert
      isMuted = true;

    }

  }
  const updateChannel = async () => {
    if (channelName && channelName !== channel.data?.name) {
      await channel.update(
        { name: channelName },
        { text: `Channel name changed to ${channelName}` },
      );
    }

    setIsEditing(false);
  };
  useEffect(() => {
   // setChatClient(client)
    setActiveChannel(channel)
  }, []);

  useEffect(() => {
    if (refreshToken) {

    //  console.log(refreshToken);
     
      const client = new StreamChat<DefaultStreamChatGenerics>(apiKey, {
        enableInsights: true,
        enableWSFallback: true,
      });

      const connectUser = async () => {
        await client?.connectUser({ id: refreshToken.UserId, eventId: refreshToken.EventId }, refreshToken.UserToken)
          .then((data) => {
            setChatClient(client);

            const channeldata = client.channel(channel!.type,channel.id);
            // Get the channel and set it in state
            setActiveChannel(channeldata);
       //     console.log('Token refreshed!' + data);
          })
          .catch((error: any) => {
        //    console.log('Failed to refresh token',error);
          });
      };
      connectUser();

    }
  }, [refreshToken]);



  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef?.current.focus();
    }
  }, [isEditing]);

  // If opened chat name is updated from backend , it must reflect in the 
  // channel header as well along with the sidebar
  useEffect(() => {
    setChannelName(channel.data?.name || '');
  }, [channel.data?.name]);

  useEffect(() => {
    if (!channelName) {

      if (!members.length || members.length === 1) {
        const member = members[0];
        // Check if this member is the current user - if so, return default name
        if (member?.user?.id === client?.user?.id) {
          setTitle('unname channel');
        }
        else {
          setTitle(member?.user?.name || member.user?.id || 'Unnamed User');
        }
      }
      
     
    }
  }, [channelName, members]);

  const EditHeader = () => (
    <form
      style={{ flex: 1 }}
      onSubmit={(event) => {
        event.preventDefault();
        inputRef?.current?.blur();
      }}
    >
      <input
        autoFocus
        className='channel-header__edit-input'
        onBlur={updateChannel}
        onChange={(event) => setChannelName(event.target.value)}
        placeholder='Type a new name for the chat'
        ref={inputRef}
        value={channelName}
      />
    </form>
  );

  /**
   * Expose PerformBackNavigation to global scope
   * Allows native platform to trigger back navigation
   */
  (window as any).PerformBackAction = () => {
    PerformBackNavigation();
  };

  /**
   * FUNCTION: getChannelIcon
   * Returns appropriate icon based on channel type and state
   * DM CHANNELS:
   * - Returns block/unblock icon (if not hidden)
   * PUBLIC CHANNELS (without membership):
   * - Returns undefined (no icon)
   * GROUP CHANNELS:
   * - Returns mute/unmute icon
   */
  const getChannelIcon = (channel:any, isBlockIconVisible:boolean, isBlocked:boolean, isMuted:boolean) => {
    if (channel.type === "messaging") {
      if (isBlockIconVisible) return undefined;
      return isBlocked ? <UnBlockUserIcon /> : <BlockUserIcon />;
    }
  
    if (channel.type === "public_without_membership" || channel.type === "public_without_membership_watchlive") {
      return undefined;
    }
  
    return isMuted ? <UnmuteChannelIcon /> : <MuteChannelIcon />;
  };

  /**
   * RENDER
   * STRUCTURE:
   * - Back button (hidden on BlazorPWA)
   * - Avatar
   * - Channel name or title
   * - Action icon (block/unblock or mute/unmute)
   * ACTION ICON CLICK:
   * - DM: Toggles block/unblock
   * - Group: Toggles mute/unmute
   */
  return (
    <div className='messaging__channel-header'>
      <div role="button" aria-label="Back button" id='mobile-nav-icon' className={`${theme}`} style={{ display: platformType == 'BlazorPWA' ? 'none' : 'block' }}
        onClick={PerformBackNavigation}>
        <ChevronLeft />
      </div>

      <AvatarChannelView data={channel} pageType={ChatPageType.ChannelDetail} />
      <div className='channel-header__name'>{channelName || title}</div>
      { }

      <div className='messaging__channel-header__right'
        onClick={() => {
          if (channel.type != "messaging") {
            muteUnmuteChannel();
          } else {
            blockUnblockUser(members[0]?.user!.id).then(() => { setShowAlert(false); });
          }

        }}>

        {
           getChannelIcon(channel, isBlockIconVisible, isBlocked, isMuted)
        }

      </div>
    </div>
  );
};

export default React.memo(MessagingChannelHeader);
